import axios from 'axios';
import config from '../config/config';

const maxNetworkRetryCount = 1;
const networkRetryDelayMs = 1500;


// Axios instance oluştur
const api = axios.create({
  baseURL: `${config.backendUrl}`,
  timeout: 30000, // 30 saniye timeout (stok işlemleri için)
  headers: {
    'Content-Type': 'application/json',
  },
});

const getActiveToken = () => {
  return localStorage.getItem('jansoft-token') || sessionStorage.getItem('jansoft-token') || null;
};

const getActiveStorage = () => {
  if (localStorage.getItem('jansoft-token')) return localStorage;
  if (sessionStorage.getItem('jansoft-token')) return sessionStorage;
  return localStorage;
};

const getActiveTenantCode = () => {
  const directTenantCode = localStorage.getItem('tenant-code') || sessionStorage.getItem('tenant-code');
  if (directTenantCode) return directTenantCode;

  try {
    const userRaw = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (userRaw) {
      const parsedUser = JSON.parse(userRaw);
      if (parsedUser?.tenantCode) {
        return parsedUser.tenantCode;
      }
    }
  } catch (_error) {
    // ignore parse issues and continue with null fallback
  }

  return null;
};

// Request interceptor - token ekle
api.interceptors.request.use(
  (config) => {
    const token = getActiveToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const tenantCode = getActiveTenantCode();
    if (tenantCode) {
      config.headers['X-Tenant-Code'] = tenantCode;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const redirectToLogin = () => {
  // Public route kontrolü - public route'larda redirect yapma
  if (isPublicRoute()) {
    return; // Public route'larda redirect yapma
  }
  
  localStorage.removeItem('jansoft-token');
  localStorage.removeItem('user');
  localStorage.removeItem('tenant-code');
  sessionStorage.removeItem('jansoft-token');
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('tenant-code');
  window.location.href = '/login';
};

const isAuthLoginRequest = (config) => {
  if (!config) return false;
  const url = config.url || '';
  return url.includes('/api/auth/login');
};

const isPublicRequest = (config) => {
  if (!config) return false;
  const url = config.url || '';
  return url.includes('/api/public/');
};

const isPublicRoute = () => {
  const publicRoutes = ['/pastel', '/cari/', '/login'];
  const currentPath = window.location.pathname;
  return publicRoutes.some(route => {
    if (route === '/cari/') {
      return currentPath.startsWith('/cari/');
    }
    return currentPath === route || currentPath.startsWith(route);
  });
};

const shouldRetryNetworkError = (error) => {
  const originalRequest = error?.config || {};
  if (isAuthLoginRequest(originalRequest) || isPublicRequest(originalRequest)) {
    return false;
  }

  const networkRetryCount = Number(originalRequest._networkRetryCount || 0);
  return networkRetryCount < maxNetworkRetryCount;
};

// Response interceptor - hata yönetimi ve retry logic + otomatik refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Network hatası durumunda otomatik retry
    const isNetworkError = error?.code === 'ERR_NETWORK' || (error?.message || '').includes('Network Error');
    if (isNetworkError && shouldRetryNetworkError(error)) {
      const originalRequest = error.config || {};
      originalRequest._networkRetryCount = Number(originalRequest._networkRetryCount || 0) + 1;
      console.log(`🔄 Bağlantı hatası, ${networkRetryDelayMs}ms sonra yeniden deneniyor...`);
      await new Promise(resolve => setTimeout(resolve, networkRetryDelayMs));
      return api.request(originalRequest);
    }

    if (error.response?.status === 401) {
      const originalRequest = error.config || {};
      
      // Public route kontrolü - eğer public route'daysak hiçbir şey yapma
      if (isAuthLoginRequest(originalRequest) || isPublicRequest(originalRequest) || isPublicRoute()) {
        return Promise.reject(error);
      }
      
      if (originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const currentToken = getActiveToken();
          if (!currentToken) {
            redirectToLogin();
            return Promise.reject(error);
          }
          const refreshResponse = await axios.post(`${config.backendUrl}/api/auth/refresh`, { token: currentToken });
          const newToken = refreshResponse.data?.token;
          if (!newToken) throw new Error('Refresh response missing token');

          const storage = getActiveStorage();
          storage.setItem('jansoft-token', newToken);

          // Update header and retry original request
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api.request(originalRequest);
        } catch (refreshErr) {
          redirectToLogin();
        }
      } else {
        redirectToLogin();
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;
